/**
 * CATALOG MODALS — модальное окно «Добавить/редактировать услугу в каталоге».
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (CATALOG TEMPLATES часть 2 — модалки/CRUD).
 *
 * Методы:
 *   - showCatalogModal()              — открыть модалку «Добавить услугу»
 *   - updateCatalogModalCategories()  — заполнить select категорий по региону
 *   - updateCatalogDayVisibility()    — показать поле «День» только для отельной категории (Phase 2)
 *   - hideCatalogModal()              — закрыть модалку, очистить форму
 *   - editTemplate(id)                — редактировать существующий услуг
 *   - duplicateTemplate(id)           — дублировать услуг (создать копию)
 *   - duplicateCurrentTemplate()      — кнопка из модалки edit
 *   - deleteTemplate(id)              — удалить услуг с confirm
 *   - addToCatalog()                  — submit формы (CREATE/UPDATE) с defaultMarkup/defaultDay (Phase 2)
 *
 * @module app/catalog-modals
 */

const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

/**
 * Прикрепляет catalog-modal методы к prototype класса.
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachCatalogModals(ClassRef) {
    Object.assign(ClassRef.prototype, {
        showCatalogModal() {
            this.editingTemplateId = null; // Reset editing state

            const modal = document.getElementById('catalog-modal');
            const modalTitle = document.getElementById('catalog-modal-title');
            const modalBtn = document.getElementById('catalog-modal-btn');
            const duplicateBtn = document.getElementById('catalog-duplicate-btn');

            if (modalTitle) modalTitle.textContent = _t('app.catalog.add_to_catalog', 'Add to catalog');
            if (modalBtn) modalBtn.textContent = _t('app.catalog.add_btn', 'Add');

            // Hide duplicate button in add mode
            if (duplicateBtn) duplicateBtn.style.display = 'none';

            // Заполняем селектор регионов
            const regionSelect = document.getElementById('catalog-region');
            if (regionSelect) {
                regionSelect.innerHTML = this.regions.map(region =>
                    `<option value="${this.escapeHtml(region)}">${this.escapeHtml(region)}</option>`
                ).join('');
                regionSelect.value = this.currentRegion;

                // При изменении региона обновляем категории
                regionSelect.onchange = () => this.updateCatalogModalCategories();
            }

            // Обновляем категории для текущего региона
            this.updateCatalogModalCategories();

            // 🆕 Group 2: вешаем listener на смену категории для toggle поля «День»
            const categorySelect = document.getElementById('catalog-category');
            if (categorySelect) {
                categorySelect.onchange = () => this.updateCatalogDayVisibility();
            }
            this.updateCatalogDayVisibility();

            // 🆕 Phase 9: загружаем suppliers в dropdown
            this.populateSupplierDropdown(null).catch(err => {
                console.warn('Failed to load suppliers:', err);
            });

            // Обновляем отображение папки
            this.updateCatalogFolderDisplay();

            if (modal) {
                modal.classList.add('show');

                setTimeout(() => {
                    const nameInput = document.getElementById('catalog-name');
                    if (nameInput) nameInput.focus();
                }, 300);
            }
        },

        /**
         * 🆕 Phase 9: Загрузить список поставщиков в dropdown #catalog-supplier-select.
         * Если selectedId передан — выставить его как текущий выбор.
         * Также обновляет общий cache this._suppliersById для рендеров.
         */
        async populateSupplierDropdown(selectedId = null) {
            const select = document.getElementById('catalog-supplier-select');
            if (!select) return;
            try {
                let raw = (window.apiClient && typeof window.apiClient.listSuppliers === 'function')
                    ? await window.apiClient.listSuppliers({ limit: 500, sort: 'company_name', dir: 'asc' })
                    : [];
                // Normalize: API возвращает {suppliers, count, ...}, иногда массив
                const list = Array.isArray(raw) ? raw : (raw.suppliers || []);
                // Sort by name (defensive — server may not honor sort)
                list.sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '', 'ru'));
                // Build options. Sprint W7 v6: mark partners with a 🤝 prefix so users see commission-bearing rows.
                const options = [`<option value="">${_t('app.catalog.supplier_not_selected', '— Not selected —')}</option>`];
                for (const s of list) {
                    const safeName = this.escapeHtml(s.company_name || '');
                    const prefix = s.is_referrer ? '🤝 ' : '';
                    const pct = s.is_referrer && s.default_commission_pct ? ` · ${s.default_commission_pct}%` : '';
                    options.push(`<option value="${s.id}" data-is-referrer="${s.is_referrer ? '1' : '0'}" data-commission="${Number(s.default_commission_pct) || 0}" data-name="${safeName}">${prefix}${safeName}${pct}</option>`);
                }
                select.innerHTML = options.join('');
                if (selectedId) select.value = selectedId;
                // Refresh cache
                this._refreshSuppliersCache(list);
                // Sprint W7 v6: wire inline commission hint
                this._updatePartnerHint();
                if (!select._partnerHintBound) {
                    select.addEventListener('change', () => this._updatePartnerHint());
                    select._partnerHintBound = true;
                }
            } catch (err) {
                console.warn('populateSupplierDropdown failed:', err);
                if (!select.options.length) {
                    select.innerHTML = `<option value="">${_t('app.catalog.supplier_not_selected', '— Not selected —')}</option>`;
                }
            }
        },

        /**
         * Sprint W7 v6: show "X% partner commission" hint under the supplier dropdown
         * when the selected supplier has is_referrer=1. Live commission preview
         * uses the price input value × default_commission_pct to display amount.
         */
        _updatePartnerHint() {
            const select = document.getElementById('catalog-supplier-select');
            const hint = document.getElementById('catalog-partner-hint');
            const hintText = document.getElementById('catalog-partner-hint-text');
            if (!select || !hint || !hintText) return;
            const opt = select.selectedOptions && select.selectedOptions[0];
            const isRef = opt && opt.dataset.isReferrer === '1';
            if (!isRef) { hint.style.display = 'none'; return; }
            const pct = Number(opt.dataset.commission) || 0;
            // i18n: read translation if loaded, fallback to RU literal
            const _t = (k, fb) => {
                const v = window.i18n?.t?.(k);
                return (v && v !== k) ? v : (fb || k);
            };
            const name = opt.dataset.name || _t('calculator.partner_hint.fallback_name', 'этот партнёр');
            const priceInput = document.getElementById('catalog-price');
            const price = priceInput ? Number(priceInput.value) || 0 : 0;
            const amount = pct > 0 ? Math.round(price * pct / 100 * 100) / 100 : 0;
            // Template: "🤝 {name} — partner. {pct}% goes to them" + optional "(≈amount of total)"
            // + auto-create note.
            const main = _t('calculator.partner_hint.main', '🤝 <strong>{name}</strong> — партнёр-рефералер. С каждого использования услуги ему уйдёт <strong>{pct}%</strong>')
                .replace('{name}', name).replace('{pct}', pct);
            const breakdown = price > 0
                ? _t('calculator.partner_hint.amount', ' (≈{amount} из {price})')
                    .replace('{amount}', amount.toLocaleString('ru-RU'))
                    .replace('{price}', price.toLocaleString('ru-RU'))
                : '';
            const tail = _t('calculator.partner_hint.tail', '. Комиссия создаётся автоматически при сохранении сметы.');
            hintText.innerHTML = main + breakdown + tail;
            hint.style.display = 'flex';
            // Re-bind on price input changes (once)
            if (priceInput && !priceInput._partnerHintBound) {
                priceInput.addEventListener('input', () => this._updatePartnerHint());
                priceInput._partnerHintBound = true;
            }
        },

        /**
         * 🆕 Phase 9: Inline-create нового поставщика прямо из модалки.
         * Используется кнопкой [+ Создать].
         */
        async createSupplierInline() {
            // Sprint 19d: полноценная modal поверх вместо prompt().
            // Минимально: только name. Все остальные поля опциональны.
            const overlay = document.createElement('div');
            overlay.setAttribute('data-modal-overlay', '');
            overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:10010;display:flex;align-items:center;justify-content:center;';
            const cancelLabel = _t('btn.cancel', 'Cancel');
            overlay.innerHTML = `
                <div style="background:white;border-radius:12px;width:90%;max-width:520px;max-height:90vh;overflow:auto;padding:20px;">
                    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
                        <h3 style="margin:0;">${_t('app.catalog.supplier_modal_title', 'New supplier')}</h3>
                        <button class="btn-icon" id="sup-modal-close" title="${_t('app.catalog.supplier_close', 'Close (ESC)')}">
                            <i data-lucide="x" style="width:16px;height:16px"></i>
                        </button>
                    </div>
                    <div style="display:flex;flex-direction:column;gap:10px;">
                        <label style="font-size:11px;color:#475569;">${_t('app.catalog.supplier_company_label', 'Company name')} <span style="color:#dc2626">*</span>
                            <input type="text" id="sup-company" class="input" required style="width:100%;margin-top:2px;" placeholder="${_t('app.catalog.supplier_company_placeholder', 'ACME Travel Ltd.')}">
                        </label>
                        <label style="font-size:11px;color:#475569;">${_t('app.catalog.supplier_type_label', 'Type')}
                            <select id="sup-type" class="input" style="width:100%;margin-top:2px;">
                                <option value="">—</option>
                                <option value="hotel">${_t('app.catalog.supplier_type_hotel', 'Hotel')}</option>
                                <option value="transport">${_t('app.catalog.supplier_type_transport', 'Transport')}</option>
                                <option value="guide">${_t('app.catalog.supplier_type_guide', 'Guide')}</option>
                                <option value="restaurant">${_t('app.catalog.supplier_type_restaurant', 'Restaurant')}</option>
                                <option value="activity">${_t('app.catalog.supplier_type_activity', 'Activity')}</option>
                                <option value="other">${_t('app.catalog.supplier_type_other', 'Other')}</option>
                            </select>
                        </label>
                        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
                            <label style="font-size:11px;color:#475569;">${_t('app.catalog.supplier_contact_name', 'Contact person')}
                                <input type="text" id="sup-contact-name" class="input" style="width:100%;margin-top:2px;">
                            </label>
                            <label style="font-size:11px;color:#475569;">${_t('app.catalog.supplier_phone', 'Phone')}
                                <input type="tel" id="sup-phone" class="input" style="width:100%;margin-top:2px;">
                            </label>
                        </div>
                        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
                            <label style="font-size:11px;color:#475569;">${_t('app.catalog.supplier_email', 'Email')}
                                <input type="email" id="sup-email" class="input" style="width:100%;margin-top:2px;">
                            </label>
                            <label style="font-size:11px;color:#475569;">${_t('app.catalog.supplier_telegram', 'Telegram')}
                                <input type="text" id="sup-telegram" class="input" style="width:100%;margin-top:2px;" placeholder="@username">
                            </label>
                        </div>
                        <label style="font-size:11px;color:#475569;">${_t('app.catalog.supplier_regions', 'Regions (comma-separated)')}
                            <input type="text" id="sup-regions" class="input" style="width:100%;margin-top:2px;" placeholder="${_t('app.catalog.supplier_regions_placeholder', 'Ushuaia, El Calafate')}">
                        </label>
                        <label style="font-size:11px;color:#475569;">${_t('app.catalog.supplier_notes', 'Notes')}
                            <textarea id="sup-notes" class="input" rows="2" style="width:100%;margin-top:2px;"></textarea>
                        </label>
                    </div>
                    <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
                        <button class="btn" id="sup-modal-cancel">${cancelLabel}</button>
                        <button class="btn btn-primary" id="sup-modal-save">${_t('app.catalog.supplier_create', 'Create')}</button>
                    </div>
                </div>
            `;
            document.body.appendChild(overlay);
            if (window.lucide) try { window.lucide.createIcons({ nodes: [overlay] }); } catch (_) {}
            const close = () => overlay.remove();
            overlay.querySelector('#sup-modal-close').addEventListener('click', close);
            overlay.querySelector('#sup-modal-cancel').addEventListener('click', close);
            overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
            const nameInput = overlay.querySelector('#sup-company');
            setTimeout(() => nameInput.focus(), 50);

            const save = async () => {
                const name = nameInput.value.trim();
                if (!name) {
                    nameInput.style.border = '1px solid #dc2626';
                    nameInput.focus();
                    return;
                }
                const data = {
                    company_name: name,
                    type: overlay.querySelector('#sup-type').value || null,
                    contact_name: overlay.querySelector('#sup-contact-name').value.trim() || null,
                    phone: overlay.querySelector('#sup-phone').value.trim() || null,
                    email: overlay.querySelector('#sup-email').value.trim() || null,
                    telegram: overlay.querySelector('#sup-telegram').value.trim() || null,
                    regions: overlay.querySelector('#sup-regions').value.split(',').map(s => s.trim()).filter(Boolean),
                    notes: overlay.querySelector('#sup-notes').value.trim() || null
                };
                try {
                    const result = await window.apiClient.createSupplier(data);
                    // createSupplier возвращает data напрямую (см. apiClient.js)
                    const newId = result?.id || result?.data?.id;
                    if (!newId) throw new Error(_t('app.catalog.supplier_create_error', 'Could not create supplier (no id in response)'));
                    await this.populateSupplierDropdown(newId);
                    this.showNotification(_t('app.catalog.supplier_created', 'Supplier "{name}" created').replace('{name}', name), false);
                    close();
                } catch (err) {
                    console.error('createSupplierInline error:', err);
                    this.showNotification(_t('app.catalog.supplier_error', 'Error: {message}').replace('{message}', err.message), true);
                }
            };
            overlay.querySelector('#sup-modal-save').addEventListener('click', save);
            // Enter в name → save (only when name field focused, иначе оставляем default)
            nameInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') { e.preventDefault(); save(); }
            });
        },

        /**
         * 🆕 Phase 9: Обновить локальный кеш { supplierId → company_name }.
         * Используется рендерами для быстрого lookup.
         */
        _refreshSuppliersCache(list) {
            if (!Array.isArray(list)) return;
            this._suppliersById = this._suppliersById || {};
            for (const s of list) {
                if (s && s.id) this._suppliersById[s.id] = s.company_name || '';
            }
        },

        /**
         * 🆕 Phase 9: Получить отображаемое имя поставщика для service/template.
         * Приоритет: supplier_id → cache → fallback на contractor строку.
         * Возвращает строку (может быть пустой).
         */
        getSupplierName(entry) {
            if (!entry) return '';
            if (entry.supplier_id && this._suppliersById && this._suppliersById[entry.supplier_id]) {
                return this._suppliersById[entry.supplier_id];
            }
            return entry.contractor || '';
        },

        /**
         * 🆕 Phase 9: Загрузить кеш поставщиков из сервера (без UI).
         * Вызывается из init() для прогрева перед рендером.
         */
        async _loadSuppliersCache() {
            try {
                if (!window.apiClient || typeof window.apiClient.listSuppliers !== 'function') return;
                const raw = await window.apiClient.listSuppliers({ limit: 500 });
                const list = Array.isArray(raw) ? raw : (raw.suppliers || []);
                this._refreshSuppliersCache(list);
            } catch (err) {
                console.warn('_loadSuppliersCache failed:', err);
            }
        },

        updateCatalogModalCategories() {
            const regionSelect = document.getElementById('catalog-region');
            const categorySelect = document.getElementById('catalog-category');

            if (!regionSelect || !categorySelect) return;
            const selectedRegion = regionSelect.value;

            const fillCategories = (categories) => {
                categorySelect.innerHTML = (categories || [])
                    .filter(cat => cat.id !== 'undefined')
                    .map(cat => `<option value="${cat.id}">${cat.icon || '📋'} ${cat.name}</option>`)
                    .join('') || `<option value="">${_t('app.catalog.no_categories', '— no categories —')}</option>`;
            };

            // Сразу рисуем из локального state (если совпадает регион)
            if (selectedRegion === this.currentRegion) {
                fillCategories(this.categories);
            } else {
                // Fallback на localStorage кэш для других регионов (мгновенно)
                const cachedRaw = localStorage.getItem(`quoteCalc_categories_${selectedRegion}`);
                if (cachedRaw) {
                    try { fillCategories(JSON.parse(cachedRaw)); } catch (_) { fillCategories([]); }
                } else {
                    fillCategories([]);
                }
            }

            // Sprint 19f: дополняем серверными данными
            if (window.apiClient && typeof window.apiClient.listRegionCategories === 'function' && selectedRegion) {
                window.apiClient.listRegionCategories(selectedRegion).then(serverCats => {
                    if (Array.isArray(serverCats) && serverCats.length) fillCategories(serverCats);
                }).catch(() => {});
            }
        },

        // 🆕 Group 2: показываем поле «День» только для отельной категории.
        //   Категория считается отельной по ID 'accommodation' либо по имени,
        //   содержащему 'отел'/'hotel'/'гостиниц' (та же логика, что в addServiceFromTemplate).
        updateCatalogDayVisibility() {
            // Sprint 19f: "День тура" всегда скрыт (убрано по фидбеку).
            const dayRow = document.getElementById('catalog-day-row');
            if (dayRow) dayRow.style.display = 'none';
        },

        hideCatalogModal() {
            const modal = document.getElementById('catalog-modal');
            const duplicateBtn = document.getElementById('catalog-duplicate-btn');

            if (modal) {
                modal.classList.remove('show');

                // Clear form and reset modal state
                ['catalog-name', 'catalog-region', 'catalog-description', 'catalog-contractor', 'catalog-price', 'catalog-markup', 'catalog-day'].forEach(id => {
                    const element = document.getElementById(id);
                    if (element) {
                        element.value = '';
                        element.classList.remove('error');
                    }
                });

                // 🆕 Phase 9: сброс supplier-dropdown
                const supplierSelect = document.getElementById('catalog-supplier-select');
                if (supplierSelect) supplierSelect.value = '';

                // Hide duplicate button
                if (duplicateBtn) duplicateBtn.style.display = 'none';

                // Hide day-row by default — оно показывается только для отелей
                const dayRow = document.getElementById('catalog-day-row');
                if (dayRow) dayRow.style.display = 'none';

                // Reset modal to add mode
                this.editingTemplateId = null;
            }
        },

        editTemplate(templateId) {
            const template = this.templates.find(t => t.id === templateId);
            if (!template) return;

            // Store the template ID being edited FIRST
            this.editingTemplateId = templateId;

            // Sprint 19f: регионы из server (через listRegions), fallback на this.regions.
            const regionSelect = document.getElementById('catalog-region');
            if (regionSelect) {
                const fillRegions = (regions) => {
                    regionSelect.innerHTML = regions.map(region =>
                        `<option value="${this.escapeHtml(region)}">${this.escapeHtml(region)}</option>`
                    ).join('');
                    regionSelect.value = template.region || this.currentRegion;
                    regionSelect.onchange = () => this.updateCatalogModalCategories();
                };
                // Сначала рисуем с локальным state — мгновенно
                fillRegions(this.regions || []);
                // Дополняем server data асинхронно
                if (window.apiClient && typeof window.apiClient.listRegions === 'function') {
                    window.apiClient.listRegions().then(serverRegions => {
                        if (serverRegions && serverRegions.length) {
                            const merged = Array.from(new Set([...(this.regions || []), ...serverRegions]));
                            fillRegions(merged);
                        }
                    }).catch(() => {});
                }
            }

            // Обновляем категории для региона услуги
            this.updateCatalogModalCategories();

            // Populate catalog modal with existing data
            const fields = {
                'catalog-name': template.name,
                'catalog-category': template.category,
                'catalog-description': template.description || '',
                'catalog-contractor': template.contractor || '',
                'catalog-price': template.price,
                // 🆕 Group 2: дефолтная наценка и (для отелей) день
                'catalog-markup': template.defaultMarkup ?? '',
                'catalog-day': template.defaultDay ?? ''
            };
            // Sprint 19h: иконка — либо из preset dropdown, либо custom Lucide name
            const iconSelect = document.getElementById('catalog-icon');
            const iconCustom = document.getElementById('catalog-icon-custom');
            const presetValues = iconSelect ? Array.from(iconSelect.options).map(o => o.value) : [];
            const tplIcon = template.icon || '';
            if (iconSelect && presetValues.includes(tplIcon)) {
                iconSelect.value = tplIcon;
                if (iconCustom) iconCustom.value = '';
            } else if (iconCustom && tplIcon) {
                iconCustom.value = tplIcon; // custom (legacy emoji или Lucide name)
            }

            Object.entries(fields).forEach(([id, value]) => {
                const element = document.getElementById(id);
                if (element) element.value = value;
            });

            // 🆕 Phase 9: загружаем suppliers и выставляем текущего
            this.populateSupplierDropdown(template.supplier_id || null).catch(err => {
                console.warn('Failed to load suppliers:', err);
            });

            // Показываем поле «День» только для отельной категории
            this.updateCatalogDayVisibility();

            // Change modal title and button text
            const modal = document.getElementById('catalog-modal');
            const modalTitle = document.getElementById('catalog-modal-title');
            const modalBtn = document.getElementById('catalog-modal-btn');
            const duplicateBtn = document.getElementById('catalog-duplicate-btn');

            if (modalTitle) modalTitle.textContent = _t('app.catalog.edit_service', 'Edit service');
            if (modalBtn) modalBtn.textContent = _t('app.catalog.save_btn', 'Save');

            // Show duplicate button in edit mode
            if (duplicateBtn) duplicateBtn.style.display = 'block';

            // Show modal WITHOUT resetting editingTemplateId
            if (modal) {
                modal.classList.add('show');

                setTimeout(() => {
                    const nameInput = document.getElementById('catalog-name');
                    if (nameInput) nameInput.focus();
                }, 300);
            }
        },

        duplicateTemplate(templateId) {
            const template = this.templates.find(t => t.id === templateId);
            if (!template) {
                this.showNotification(_t('app.catalog.service_not_found', 'Service not found'), true);
                return;
            }

            // Создать копию с новым ID и пометкой "(копия)"
            const duplicatedTemplate = {
                ...template,
                id: Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9),
                name: template.name + ' ' + _t('app.catalog.copy_suffix', '(copy)')
            };

            // Добавить копию в массив услуг
            this.templates.push(duplicatedTemplate);

            // Сохранить и перерендерить
            this.renderTemplates();
            this.saveToLocalStorage();

            this.showNotification(
                _t('app.catalog.service_duplicated', '📋 Service "{name}" duplicated').replace('{name}', template.name)
            );
        },

        duplicateCurrentTemplate() {
            if (!this.editingTemplateId) {
                this.showNotification(_t('app.catalog.no_service_to_duplicate', 'No service selected to duplicate'), true);
                return;
            }

            // Дублировать текущий редактируемый услуг
            this.duplicateTemplate(this.editingTemplateId);

            // Закрыть модальное окно
            this.hideCatalogModal();
        },

        async deleteTemplate(templateId) {
            const template = this.templates.find(t => t.id === templateId);
            if (!template) return;

            const { confirmModal } = await import('/public/js/components/text-prompt-modal.js');
            const ok = await confirmModal({
                title: _t('app.catalog.delete_confirm_title', 'Remove service from catalog?'),
                message: _t('app.catalog.delete_confirm_message', 'Remove "{name}" from the catalog?').replace('{name}', template.name),
                danger: true
            });
            if (!ok) return;

            this.templates = this.templates.filter(t => t.id !== templateId);
            this.renderTemplates();
            this.saveToLocalStorage();
            this.showNotification(_t('app.catalog.service_deleted', 'Service removed from the catalog'));
        },

        addToCatalog() {
            const nameElement = document.getElementById('catalog-name');
            const regionElement = document.getElementById('catalog-region');
            const categoryElement = document.getElementById('catalog-category');
            const descriptionElement = document.getElementById('catalog-description');
            const contractorElement = document.getElementById('catalog-contractor');
            const iconElement = document.getElementById('catalog-icon');
            const priceElement = document.getElementById('catalog-price');
            // 🆕 Group 2: дефолтная наценка из услуги + порядковый день для отелей
            const markupElement = document.getElementById('catalog-markup');
            const dayElement = document.getElementById('catalog-day');
            // 🆕 Phase 9: supplier dropdown
            const supplierSelectEl = document.getElementById('catalog-supplier-select');

            if (!nameElement || !regionElement || !categoryElement || !iconElement || !priceElement) return;

            const name = nameElement.value.trim();
            const selectedRegion = regionElement.value;
            const category = categoryElement.value;
            const description = descriptionElement ? descriptionElement.value.trim() : '';
            // 🆕 Phase 9: вычисляем supplier_id и contractor name из dropdown'а
            const supplierId = supplierSelectEl ? (supplierSelectEl.value || null) : null;
            let contractor = contractorElement ? (contractorElement.value || '').trim() : '';
            if (supplierId) {
                // Из dropdown'а берём отображаемое имя (для backward-compat рендеров)
                const selectedOption = supplierSelectEl.options[supplierSelectEl.selectedIndex];
                if (selectedOption) contractor = (selectedOption.textContent || '').trim();
                // Доп. fallback: cache
                if (!contractor && this._suppliersById && this._suppliersById[supplierId]) {
                    contractor = this._suppliersById[supplierId];
                }
            }
            // Sprint 19h: иконка может быть из dropdown ИЛИ кастомная Lucide
            const customIconEl = document.getElementById('catalog-icon-custom');
            const customIcon = customIconEl ? customIconEl.value.trim() : '';
            const icon = customIcon || iconElement.value;
            const price = Math.max(0, parseFloat(priceElement.value) || 0);
            // Наценка по умолчанию: 0..500%, пустое = 0
            const defaultMarkup = markupElement
                ? Math.max(0, Math.min(500, parseFloat(markupElement.value) || 0))
                : 0;
            // День (только для отелей): 1..N или null
            const defaultDay = (dayElement && dayElement.value !== '')
                ? Math.max(1, parseInt(dayElement.value, 10) || 1)
                : null;

            if (!name) {
                this.showNotification(_t('app.catalog.enter_service_name', 'Enter service name'), true);
                nameElement.classList.add('error');
                return;
            }

            if (!category || category === '') {
                this.showNotification(_t('app.catalog.select_category', 'Select a category'), true);
                categoryElement.classList.add('error');
                return;
            }

            if (price <= 0) {
                this.showNotification(_t('app.catalog.enter_valid_price', 'Enter a valid price'), true);
                priceElement.classList.add('error');
                return;
            }

            if (this.editingTemplateId) {
                // Update existing template
                const oldRegion = this.currentRegion;
                const template = this.templates.find(t => t.id === this.editingTemplateId);

                if (template) {
                    const oldTemplateRegion = template.region || oldRegion;

                    // Обновляем данные услуги
                    template.name = name;
                    template.category = category;
                    template.description = description;
                    template.contractor = contractor;
                    // 🆕 Phase 9: supplier_id привязка (или очистка если выбрано «— Не выбран —»)
                    if (supplierId) {
                        template.supplier_id = supplierId;
                    } else {
                        delete template.supplier_id;
                    }
                    template.icon = icon;
                    template.price = price;
                    template.region = selectedRegion;
                    template.defaultMarkup = defaultMarkup;
                    template.defaultDay = defaultDay; // null для не-отелей

                    // Если регион изменился - переносим услугу
                    if (oldTemplateRegion !== selectedRegion) {
                        // Удаляем из старого региона
                        const oldTemplatesKey = `quoteCalc_templates_${oldTemplateRegion}`;
                        let oldTemplates = JSON.parse(localStorage.getItem(oldTemplatesKey) || '[]');
                        oldTemplates = oldTemplates.filter(t => t.id !== this.editingTemplateId);
                        localStorage.setItem(oldTemplatesKey, JSON.stringify(oldTemplates));

                        // Добавляем в новый регион
                        const newTemplatesKey = `quoteCalc_templates_${selectedRegion}`;
                        let newTemplates = JSON.parse(localStorage.getItem(newTemplatesKey) || '[]');
                        newTemplates.push(template);
                        localStorage.setItem(newTemplatesKey, JSON.stringify(newTemplates));

                        // Обновляем текущий каталог если смотрим на один из этих регионов
                        if (this.currentRegion === oldTemplateRegion) {
                            this.templates = this.templates.filter(t => t.id !== this.editingTemplateId);
                        } else if (this.currentRegion === selectedRegion) {
                            this.templates.push(template);
                        }

                        this.showNotification(
                            _t('app.catalog.service_moved', 'Service moved to region "{region}"').replace('{region}', selectedRegion)
                        );
                    } else {
                        // Регион не изменился - просто сохраняем каталог
                        this.saveCatalogToRegion(selectedRegion).catch(err => {
                            console.error('Failed to save catalog:', err);
                        });
                        this.showNotification(_t('app.catalog.service_updated', 'Service updated'));
                    }

                    console.log('Template updated:', template);
                }
            } else {
                // Add new template
                const template = {
                    id: 'custom_' + Date.now(),
                    name: name,
                    category: category,
                    description: description,
                    contractor: contractor,
                    icon: icon,
                    price: price,
                    region: selectedRegion,
                    defaultMarkup: defaultMarkup,
                    defaultDay: defaultDay
                };
                // 🆕 Phase 9: supplier_id привязка (только если выбран в dropdown'е)
                if (supplierId) template.supplier_id = supplierId;

                // ✅ Добавляем услуг в память и сохраняем на сервер
                // Если добавляем в текущий регион - обновляем отображение
                if (selectedRegion === this.currentRegion) {
                    this.templates.push(template);
                }

                // Сохраняем каталог на сервер после добавления услуги
                this.saveCatalogToRegion(selectedRegion).catch(err => {
                    console.error('Failed to save catalog:', err);
                });

                this.showNotification(
                    _t('app.catalog.service_added', 'Service added to catalog of region "{region}"').replace('{region}', selectedRegion)
                );
                console.log('Added new template to region:', selectedRegion, template);
            }

            this.renderTemplates();
            this.hideCatalogModal();
        },
    });
}
